/**
 *  @class
 *  @function Predictive Search
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();

    }
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.predictiveSearchResults = this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');

      this.setupEventListeners();

    }
    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
      // PR-C5c: robust "close on real focus loss" — relatedTarget is null when
      // clicking on non-focusable elements (filter header <div>s, labels, etc.)
      // so a simple contain check was insufficient. Defer to a setTimeout so
      // activeElement has time to settle, then check if focus landed anywhere
      // inside <search-form>. If yes → user is interacting with the dropdown,
      // stay open. If no → real dismiss, close.
      this.addEventListener('focusout', () => {
        setTimeout(() => {
          // Skip close when the whole WINDOW just lost focus (user
          // Alt-tabbed to another app, clicked the URL bar, opened a
          // new tab, etc.). Wait for them to click somewhere on the
          // page before deciding to dismiss. Fixes "click away from
          // window and back closes the dropdown".
          if (!document.hasFocus()) return;
          if (!this.contains(document.activeElement)) {
            this.close();
          }
        }, 0);
      });
      // Preserve focus on the search input when the user clicks on
      // non-focusable whitespace INSIDE the dropdown (blank space between
      // filter groups, gaps in the middle column, etc.). Without this,
      // the click moves activeElement to <body>, focusout fires, and the
      // dropdown closes even though the user is still interacting with
      // it. preventDefault on mousedown suppresses only the focus side-
      // effect — click events still fire, so checkboxes / buttons /
      // chips / product links all continue to work.
      this.predictiveSearchResults.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return; // left-click only
        // Allow default focus movement to real text inputs / textareas /
        // contenteditable — none inside the dropdown today, but future-proof.
        if (e.target && e.target.matches
            && e.target.matches('input[type="text"], input[type="search"], textarea, [contenteditable="true"]')) return;
        e.preventDefault();
      });

      // MVP-B search analytics — delegated click capture for dropdown card
      // clicks (spec § 4.1: dropdown_suggestion_click / dropdown_product_click
      // / dropdown_article_click). Bound ONCE on this.predictiveSearchResults
      // (the persistent container — only its innerHTML changes per render),
      // so we don't need to rebind per fetch. Fired synchronously in the
      // capture phase so the envelope lands before the page unloads.
      this.predictiveSearchResults.addEventListener('click', (e) => {
        try {
          const api = window.AroSearchAPI;
          if (!api || typeof api.trackAroSearchEvent !== 'function') return;
          const searchTerm = this.getQuery();

          // Autocomplete suggestion click (SUGGESTIONS block)
          const suggestion = e.target.closest && e.target.closest('.aro-search-dropdown__suggestion');
          if (suggestion) {
            const list = Array.prototype.slice.call(
              this.predictiveSearchResults.querySelectorAll('.aro-search-dropdown__suggestion')
            );
            const idx = list.indexOf(suggestion);
            const text = (suggestion.textContent || '').trim();
            api.trackAroSearchEvent('dropdown_suggestion_click', {
              search_term: searchTerm,
              suggestion_text: text,
              suggestion_index: idx
            });
            return;
          }

          // Product card click — any anchor inside .aro-search-dropdown__products
          // whose href points to /products/<handle>. Extract handle from URL;
          // product_id left null (spec § 4.1 accepts null — join at report time).
          const productsWrap = e.target.closest && e.target.closest('.aro-search-dropdown__products');
          if (productsWrap) {
            const anchor = e.target.closest && e.target.closest('a[href*="/products/"]');
            if (anchor) {
              const cards = Array.prototype.slice.call(productsWrap.children);
              const card = anchor.closest && (anchor.closest('product-card') || anchor.closest('.product-card'));
              const pos = card ? cards.indexOf(card) : -1;
              const href = anchor.getAttribute('href') || '';
              const match = href.match(/\/products\/([^/?#]+)/);
              api.trackAroSearchEvent('dropdown_product_click', {
                search_term: searchTerm,
                product_handle: match ? match[1] : null,
                product_id: null,
                position: pos
              });
              return;
            }
          }

          // Blog / recipe article card click — .aro-search-dropdown__article
          // sits inside .aro-search-dropdown__panel--blogs or --recipes.
          const article = e.target.closest && e.target.closest('.aro-search-dropdown__article');
          if (article) {
            const panelEl = article.closest && article.closest('.aro-search-dropdown__panel');
            let panel = null;
            if (panelEl) {
              if (panelEl.classList.contains('aro-search-dropdown__panel--recipes')) panel = 'recipes';
              else if (panelEl.classList.contains('aro-search-dropdown__panel--blogs')) panel = 'blogs';
            }
            const siblings = panelEl
              ? Array.prototype.slice.call(panelEl.querySelectorAll('.aro-search-dropdown__article'))
              : [];
            const pos = siblings.indexOf(article);
            api.trackAroSearchEvent('dropdown_article_click', {
              search_term: searchTerm,
              article_url: article.getAttribute('href') || null,
              panel: panel,
              position: pos
            });
            return;
          }
        } catch (e) { /* analytics never blocks navigation */ }
      }, true); // capture phase — must run before <a> default navigation

      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));

      this.cc.addEventListener('click', this.close.bind(this));

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.close();
          }
        }
      });
      // <input type="search"> has a browser-native behaviour where pressing
      // Escape clears the value. We want Escape to close the dropdown but
      // preserve the typed query so the user can amend it and re-search
      // without retyping. preventDefault on keydown blocks the native clear;
      // the document-level keyup handler above still runs .close().
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' || e.code === 'Escape') {
          e.preventDefault();
        }
      });
      // Focus opens the dropdown; if the input already has text (user
       // came back to the page with a stale query, or clicked back into
       // the input after typing), also fire the typing-state fetch so
       // the full 3-col results view appears instead of the empty focus
       // state. Reveals results for existing text on click.
      this.input.addEventListener('focus', () => {
        this.open();
        this.onFocus();
      });

      // PR-C8: while the mouse is over the dropdown, prevent wheel/touchmove
      // events from scrolling the page behind. If the wheel target sits inside
      // a scrollable column that CAN still scroll in the wheel direction, let
      // the browser handle it natively (no preventDefault). If the column is
      // pegged at top/bottom, or the target isn't inside any scrollable child,
      // block the event so the page doesn't scroll-chain.
      var self = this;
      var handleWheel = function (e) {
        if (!self.predictiveSearchResults || self.predictiveSearchResults.hasAttribute('inert')) return;
        var deltaY = e.deltaY;
        if (deltaY === 0) return;
        var el = e.target;
        while (el && el !== self.predictiveSearchResults) {
          var canScroll = el.scrollHeight > el.clientHeight;
          if (canScroll) {
            var atTop = el.scrollTop <= 0;
            var atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
            if ((deltaY < 0 && atTop) || (deltaY > 0 && atBottom)) {
              e.preventDefault();
            }
            return;
          }
          el = el.parentElement;
        }
        // No scrollable ancestor found — block page-scroll
        e.preventDefault();
      };
      this.predictiveSearchResults.addEventListener('wheel', handleWheel, { passive: false });
      // Same idea for touch — but touch scroll semantics are different. Simpler:
      // once the touch starts inside the dropdown, block touchmove from bubbling
      // to page. Inner column native scroll still works because touchmove on the
      // column itself is the one being intercepted here, and preventDefault at
      // the wrapper only fires when target is outside scrollable children.
      this.predictiveSearchResults.addEventListener('touchmove', function (e) {
        if (!self.predictiveSearchResults || self.predictiveSearchResults.hasAttribute('inert')) return;
        var el = e.target;
        while (el && el !== self.predictiveSearchResults) {
          if (el.scrollHeight > el.clientHeight) return;
          el = el.parentElement;
        }
        e.preventDefault();
      }, { passive: false });
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        document.body.classList.remove('open-search');
        this.predictiveSearchResults.setAttribute('inert', '');
        return;
      }
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
      this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
        return;
      }
      // F13 analytics — fires exactly what CC used to fire (intelliSearch
      // dataLayer + gtag pushes with search_term). See
      // native-search-api.js trackSearchEvent. Synchronous push before
      // navigation so the event lands before the page unloads.
      // Only fires from the header form submit — matches CC parity.
      try {
        if (window.AroSearchAPI && typeof window.AroSearchAPI.trackSearchEvent === 'function') {
          window.AroSearchAPI.trackSearchEvent(this.getQuery());
        }
      } catch (e) { /* analytics failure must never block submit */ }
    }

    onFocus() {
      const searchTerm = this.getQuery();

      // MVP-B search analytics — dropdown_opened fires on every header
      // focus, regardless of whether a query is present (spec § 4.1).
      // Placed BEFORE the empty-query early return so both empty and
      // with-query focuses are captured.
      try {
        if (window.AroSearchAPI && typeof window.AroSearchAPI.trackAroSearchEvent === 'function') {
          window.AroSearchAPI.trackAroSearchEvent('dropdown_opened', {
            has_query: searchTerm.length > 0
          });
        }
      } catch (e) { /* analytics never blocks UX */ }

      if (!searchTerm.length) {
        return;
      }

      this.getSearchResults(searchTerm);
    }

    getSearchResults(searchTerm) {

      this.predictiveSearchResults.classList.add('loading');

      // PR-C2 (feat/native-intellisearch-full-search-top9, Dev/cc-intellisearch-replacement/):
      // Switched from /search/suggest.json (predictive — narrow exact match, returned few
      // results) to /search (full search — broad match, same results as the /search page).
      // The section (sections/predictive-search.liquid) now uses `search.results` instead of
      // `predictive_search.resources.*` and caps to top 9 products / top 5 articles.
      //
      // PR-C6 (feat/native-intellisearch-suggestions-parallel-fetch): full-search endpoint
      // doesn't return autocomplete `queries` so the SUGGESTIONS block disappeared. Fire a
      // parallel fetch to /search/suggest.json?resources[type]=query for just the autocomplete
      // terms, then inject them into a placeholder inside the rendered section.
      //
      // PR-Dropdown-preserve-filter: include currently-checked filter inputs in the
      // products URL. Without this, any getSearchResults call that happens AFTER a
      // filter click (focus refresh, debounced input handler, etc.) fetches the
      // unfiltered set and the resulting renderSearchResults wipes the user's
      // filter selection. Article/query URLs deliberately do NOT include filters —
      // Blogs/Recipes and autocomplete are independent of product filter state.
      var productParams = new URLSearchParams();
      productParams.append('q', searchTerm);
      productParams.append('section_id', 'predictive-search');
      this._collectActiveFilterParams(productParams);
      var searchUrl = theme.routes.search_url + '?' + productParams.toString();
      // PR-C6b: theme.routes.predictive_search_url is '/search/suggest' (HTML endpoint).
      // We want '/search/suggest.json' for the parallel autocomplete-queries fetch.
      var queryUrl = theme.routes.predictive_search_url + '.json?q=' + encodeURIComponent(searchTerm) + '&resources[type]=query&resources[limit]=5';
      // PR-Dropdown-articles-section (Tomas 2026-08-06): supersedes the
      // suggest.json articles fetch (which was hard-capped at 10 by Shopify
      // regardless of the limit param → recipe panel routinely stuck at 5
      // even though the section server-renders 9). Fire a second section
      // fetch with &type=article so search.results is article-only, giving
      // all 50 paginated slots to articles. Section then server-renders
      // full Blogs (cap 12) and Recipes (cap 9) panels; JS extracts them
      // + merges with tag-fetches.
      var articleSectionUrl = theme.routes.search_url + '?q=' + encodeURIComponent(searchTerm) + '&type=article&section_id=predictive-search';

      var self = this;
      Promise.all([
        fetch(searchUrl).then(function (r) {
          if (!r.ok) throw new Error('search HTTP ' + r.status);
          return r.text();
        }),
        fetch(queryUrl).then(function (r) {
          if (!r.ok) return null;
          return r.json();
        }).catch(function () { return null; }),
        fetch(articleSectionUrl).then(function (r) {
          if (!r.ok) return null;
          return r.text();
        }).catch(function () { return null; }),
        // PR-C11 (revised): per-matched-tag fetches for Products-scope recipes.
        self.fetchTagMatchedRecipes(searchTerm),
        // PR-C13: same treatment for blog articles — expand returns via tag
        // match. No filter UI for blogs, just add matched-tag articles to the
        // Shopify suggest result set.
        self.fetchTagMatchedBlogArticles(searchTerm),
      ]).then(function (results) {
        self.predictiveSearchResults.classList.remove('loading');
        var html = results[0];
        var queriesJson = results[1];
        var articleSectionHtml = results[2];
        var tagMatchedRecipes = results[3] || [];
        var tagMatchedBlogs = results[4] || [];
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var section = doc.querySelector('#shopify-section-predictive-search');
        if (!section) return;
        self.renderSearchResults(section.innerHTML);
        var qResults = queriesJson && queriesJson.resources && queriesJson.resources.results;
        if (qResults) {
          self.renderQueries(qResults.queries || []);
        }
        // "Did you mean X?" — populate the products-panel empty-state
        // when Shopify returned zero products AND the query suggestions
        // include something that differs from the typed term. Reuses the
        // suggest.json response already fetched for the SUGGESTIONS block
        // — no extra network round-trip.
        self._populateDidYouMean(searchTerm, qResults ? (qResults.queries || []) : []);
        // Parse article-only section HTML for full blog+recipe article lists.
        // Section server-renders .aro-search-dropdown__article links (up to
        // cap per prefix). Convert to article-object shape so renderArticle-
        // Panels can merge with tag-matches + apply scope classification.
        var arts = self.extractArticlesFromSection(articleSectionHtml);
        self.renderArticlePanels(arts, tagMatchedRecipes, tagMatchedBlogs, searchTerm);
        // PR-C15: cache so onFilterChange (which re-fetches the whole
        // section and wipes blog/recipe panel HTML) can re-render article
        // panels from the cached data — product filters must NOT affect
        // Blogs/Recipes results.
        self._lastArticles = arts;
        self._lastTagMatchedRecipes = tagMatchedRecipes;
        self._lastTagMatchedBlogs = tagMatchedBlogs;
        self._lastSearchTerm = searchTerm;

        // MVP-A search analytics — fire ONE dropdown_query event end-to-end
        // to prove the pipeline (spec § 9.1). Counts read from the DOM after
        // renderSearchResults + renderArticlePanels have both completed, so
        // they reflect what the user actually sees. Failure never blocks the
        // dropdown — analytics is fire-and-forget. See native-search-api.js
        // trackAroSearchEvent for envelope shape + session identity.
        try {
          if (window.AroSearchAPI && typeof window.AroSearchAPI.trackAroSearchEvent === 'function') {
            var productsContainer = self.predictiveSearchResults.querySelector('.aro-search-dropdown__products');
            var blogPanelEl = self.predictiveSearchResults.querySelector('.aro-search-dropdown__panel--blogs');
            var recipePanelEl = self.predictiveSearchResults.querySelector('.aro-search-dropdown__panel--recipes');
            var productCount = productsContainer ? productsContainer.children.length : 0;
            var blogCount = blogPanelEl ? blogPanelEl.querySelectorAll('.aro-search-dropdown__article').length : 0;
            var recipeCount = recipePanelEl ? recipePanelEl.querySelectorAll('.aro-search-dropdown__article').length : 0;
            window.AroSearchAPI.trackAroSearchEvent('dropdown_query', {
              search_term: searchTerm,
              product_count: productCount,
              blog_count: blogCount,
              recipe_count: recipeCount
            });
          }
        } catch (e) { /* analytics failure must never break search UX */ }
      }).catch(function (err) {
        self.predictiveSearchResults.classList.remove('loading');
        console.warn('[predictive-search] fetch failed:', err);
      });
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;

      // PR-C17: numeric-sort Size subgroup facets. Shopify returns option
      // values alphabetically so "1 Kg" precedes "2 g" and "500 g" ends up
      // after "50 g". Convert each label to a common base unit (grams for
      // Size (Weight), ml for Size (Volume)) and reorder facets in DOM.
      var sizeParse = function (label, isWeight) {
        var num = parseFloat(label);
        if (isNaN(num)) return 0;
        var l = label.toLowerCase();
        if (isWeight) {
          if (l.indexOf('kg') !== -1 || l.indexOf('kilogram') !== -1) return num * 1000;
          return num; // grams
        }
        if (l.indexOf('ml') !== -1) return num;
        if (l.indexOf('litre') !== -1 || l.indexOf('liter') !== -1) return num * 1000;
        if (/\b\d[\d.]*\s*l\b/.test(l)) return num * 1000; // "1 L" / "2.5 L"
        return num;
      };
      this.predictiveSearchResults.querySelectorAll('.aro-search-dropdown__filter').forEach(function (filter) {
        var titleEl = filter.querySelector('.aro-search-dropdown__filter-title span');
        if (!titleEl) return;
        var titleText = titleEl.textContent.trim().toLowerCase();
        if (titleText.indexOf('size') === -1) return;
        var isWeight = titleText.indexOf('weight') !== -1;
        var container = filter.querySelector('.aro-search-dropdown__filter-facets');
        if (!container) return;
        var facets = Array.prototype.slice.call(container.querySelectorAll('.aro-search-dropdown__facet'));
        facets.sort(function (a, b) {
          var aLabelEl = a.querySelector('.aro-search-dropdown__facet-label');
          var bLabelEl = b.querySelector('.aro-search-dropdown__facet-label');
          var aLabel = aLabelEl ? aLabelEl.textContent : '';
          var bLabel = bLabelEl ? bLabelEl.textContent : '';
          return sizeParse(aLabel, isWeight) - sizeParse(bLabel, isWeight);
        });
        facets.forEach(function (f) { container.appendChild(f); });
      });

      // PR-C9c: preserve which tab (Products / Blogs / Recipes) was checked
      // across re-renders. innerHTML replacement resets CSS-radio state back
      // to Products (Liquid default). Restore if user has picked one.
      // Session-wide persistence: if no in-instance selection yet, read from
      // sessionStorage (shared with /search page tabs) so tab persists
      // across navigations — searching a recipe, clicking a result, then
      // searching again re-opens the dropdown on the Recipes tab.
      if (!this.selectedTabId) {
        try {
          var savedName = sessionStorage.getItem('aro-search-page-tab');
          if (savedName && ['products','blogs','recipes'].indexOf(savedName) !== -1) {
            this.selectedTabId = 'aro-search-tab-' + savedName;
          }
        } catch (e) { /* silent */ }
      }
      if (this.selectedTabId) {
        var savedTab = this.predictiveSearchResults.querySelector('#' + this.selectedTabId);
        if (savedTab) savedTab.checked = true;
      }
      var self = this;
      this.predictiveSearchResults.querySelectorAll('input[name="aro-search-tab"]').forEach(function (radio) {
        radio.addEventListener('change', function () {
          if (!radio.checked) return;
          // MVP-B search analytics — dropdown_tab_switch (spec § 4.1).
          // Compute from/to BEFORE mutating self.selectedTabId.
          try {
            if (window.AroSearchAPI && typeof window.AroSearchAPI.trackAroSearchEvent === 'function') {
              var from = self.selectedTabId ? self.selectedTabId.replace('aro-search-tab-', '') : null;
              var to = radio.id.replace('aro-search-tab-', '');
              if (from !== to) {
                window.AroSearchAPI.trackAroSearchEvent('dropdown_tab_switch', {
                  search_term: self.getQuery(),
                  from_tab: from,
                  to_tab: to
                });
              }
            }
          } catch (e) { /* analytics never blocks UX */ }

          self.selectedTabId = radio.id;
          // Phase 8b: hand off to /search page tab persistence — when user
          // navigates from dropdown to full search page (Enter / View all
          // / clicking a filter), the page opens on the same tab they were
          // on here. Uses same sessionStorage key as aro-search-page-tabs.js.
          try {
            var tab = radio.id.replace('aro-search-tab-', '');
            if (['products','blogs','recipes'].indexOf(tab) !== -1) {
              sessionStorage.setItem('aro-search-page-tab', tab);
            }
          } catch (e) { /* silent */ }
        });
      });

      // Restore user's filter group open/closed preferences after each
      // re-render. State is mirrored between dropdown + /search page via
      // AroSearchAPI.getOpenFilterGroups (localStorage). If null (user
      // hasn't toggled anywhere yet), leave Liquid's default state intact.
      // Otherwise fully override: matching labels open, everything else
      // closed.
      var savedGroups = (window.AroSearchAPI && typeof window.AroSearchAPI.getOpenFilterGroups === 'function')
        ? window.AroSearchAPI.getOpenFilterGroups() : null;
      if (savedGroups) {
        this.predictiveSearchResults.querySelectorAll('.aro-search-dropdown__filter').forEach(function (filter) {
          var labelEl = filter.querySelector('.aro-search-dropdown__filter-title span');
          if (!labelEl) return;
          var label = labelEl.textContent.trim();
          var shouldOpen = savedGroups.has(label);
          filter.classList.toggle('aro-search-dropdown__filter--open', shouldOpen);
          var toggle = filter.querySelector('.aro-search-dropdown__filter-toggle');
          if (toggle) toggle.textContent = shouldOpen ? '−' : '+';
        });
      }

      let submitButton = this.querySelector('#search-results-submit');

      if (submitButton) {
        submitButton.addEventListener('click', () => {
          // MVP-B search analytics — dropdown_view_all_click (spec § 4.1).
          // Fire synchronously so envelope lands before form.submit()
          // navigates the page.
          try {
            if (window.AroSearchAPI && typeof window.AroSearchAPI.trackAroSearchEvent === 'function') {
              window.AroSearchAPI.trackAroSearchEvent('dropdown_view_all_click', {
                search_term: this.getQuery()
              });
            }
          } catch (e) { /* analytics never blocks nav */ }
          this.form.submit();
        });
      }

      // PR-C5: bind live filter facets + header toggle. Event delegation would be
      // cleaner but predictive-search rewrites innerHTML on every keystroke, so
      // rebinding per-render is the simplest correct behaviour.
      var self = this;
      this.predictiveSearchResults.querySelectorAll('[data-aro-filter]').forEach(function (inp) {
        inp.addEventListener('change', function () {
          // MVP-B search analytics — dropdown_filter_toggle (spec § 4.1).
          // filter_name = facet GROUP title (e.g. "Collection", "Availability")
          //   read from the nearest .aro-search-dropdown__filter-title span.
          //   `data-aro-filter` on the input itself is just an empty marker
          //   attribute — not a group name (verified DOM 2026-08-12).
          // filter_value = the input.value Shopify wrote (e.g. "Active Ingredients")
          try {
            if (window.AroSearchAPI && typeof window.AroSearchAPI.trackAroSearchEvent === 'function') {
              var group = inp.closest('.aro-search-dropdown__filter');
              var titleEl = group && group.querySelector('.aro-search-dropdown__filter-title span');
              var filterName = titleEl ? (titleEl.textContent || '').trim() : null;
              window.AroSearchAPI.trackAroSearchEvent('dropdown_filter_toggle', {
                search_term: self.getQuery(),
                filter_name: filterName,
                filter_value: inp.value || null,
                filter_param: inp.name || null,
                state: inp.checked ? 'on' : 'off'
              });
            }
          } catch (e) { /* analytics never blocks UX */ }

          // In stock ↔ Out of stock mutex. Uncheck the sibling BEFORE
          // capture / onFilterChange read the DOM, so the URL never has
          // both filter.v.availability=1 AND filter.p.tag=_fully-oos (a
          // contradictory query that returns 0 results).
          self._enforceAvailabilityMutex(inp);
          // Capture ALL currently-checked filter inputs so we can re-apply
          // them after render. Zero-result narrows drop Shopify's
          // value.active, which would otherwise silently lose other
          // filters (e.g. Essential Oils + OOS = 0 → next fetch drops
          // Essential Oils because its checkbox comes back unchecked).
          self._captureFilterState();
          self.onFilterChange();
        });
      });
      // Re-apply captured filter selections on every render. Uses
      // data-aro-carry hidden inputs when a checkbox is missing from the
      // fresh DOM (single source of truth for the next fetch URL).
      this._restoreFilterState();
      // Inject the Availability chip when the OOS/In stock checkbox is
      // checked but Shopify didn't render a corresponding chip. Shopify's
      // filter.values inconsistently exposes _fully-oos as active
      // (varies by query and result count), so we can't rely on the
      // server-side chip loop. Only fires when a chip row already exists
      // (i.e. another filter is also applied) so we don't have to
      // construct the whole active-filters row from scratch.
      this._syncAvailabilityChip();
      // PR-C10: recipe-scope filter checkboxes — restore state, bind change
      // handlers, wire the "Search in" header to same open/close pattern as
      // product filters (via data-aro-recipe-filter-toggle).
      var currentScopes = self.getRecipeScopeState();
      this.predictiveSearchResults.querySelectorAll('[data-aro-recipe-scope]').forEach(function (cb) {
        var name = cb.getAttribute('data-aro-recipe-scope');
        cb.checked = currentScopes[name] !== false; // default true
        cb.addEventListener('change', function () {
          if (!self.recipeScopes) self.recipeScopes = { title: true, description: true, body: true, products: true };
          self.recipeScopes[name] = cb.checked;
          self.applyRecipeScopeFilter();
        });
      });
      this.predictiveSearchResults.querySelectorAll('[data-aro-recipe-filter-toggle]').forEach(function (header) {
        header.addEventListener('click', function () {
          var filter = header.closest('.aro-search-dropdown__filter');
          if (!filter) return;
          var isOpen = filter.classList.toggle('aro-search-dropdown__filter--open');
          var toggle = header.querySelector('.aro-search-dropdown__filter-toggle');
          if (toggle) toggle.innerHTML = isOpen ? '&minus;' : '+';
        });
      });

      this.predictiveSearchResults.querySelectorAll('[data-aro-filter-toggle]').forEach(function (header) {
        header.addEventListener('click', function () {
          var filter = header.closest('.aro-search-dropdown__filter');
          if (!filter) return;
          filter.classList.toggle('aro-search-dropdown__filter--open');
          var toggle = header.querySelector('.aro-search-dropdown__filter-toggle');
          var isOpen = filter.classList.contains('aro-search-dropdown__filter--open');
          if (toggle) {
            toggle.textContent = isOpen ? '−' : '+';
          }
          // Persist to AroSearchAPI (localStorage) so the /search page
          // sidebar picks up the same open/closed state. On the first
          // user toggle, seed the Set from the current Liquid-default
          // open groups so subsequent re-renders apply the user's
          // ACTUAL preferences (not just what they've toggled since
          // page load).
          var api = window.AroSearchAPI;
          if (!api || typeof api.setOpenFilterGroups !== 'function') return;
          var groups = api.getOpenFilterGroups();
          if (!groups) {
            groups = new Set();
            self.predictiveSearchResults.querySelectorAll('.aro-search-dropdown__filter--open').forEach(function (openFilter) {
              var lbl = openFilter.querySelector('.aro-search-dropdown__filter-title span');
              if (lbl) groups.add(lbl.textContent.trim());
            });
          }
          var titleSpan = header.querySelector('span');
          if (titleSpan) {
            var label = titleSpan.textContent.trim();
            if (isOpen) groups.add(label);
            else groups.delete(label);
          }
          api.setOpenFilterGroups(groups);
        });
      });
      // PR-C5b: active-filter chip removal + "Clear all".
      this.predictiveSearchResults.querySelectorAll('[data-aro-remove-filter]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var name = btn.getAttribute('data-aro-remove-filter');
          var value = btn.getAttribute('data-aro-remove-value');
          // Uncheck ALL matching inputs — defensive against duplicates
          // (e.g. if Shopify's Product Tag facet ever exposes _fully-oos
          // in "More filters" alongside our custom Availability facet).
          // A single querySelector would only uncheck the first match,
          // leaving the second in the URL and making the chip look stuck.
          self.predictiveSearchResults.querySelectorAll(
            'input[data-aro-filter][name="' + CSS.escape(name) + '"][value="' + CSS.escape(value) + '"]'
          ).forEach(function (input) {
            if (input.type === 'checkbox') input.checked = false;
          });
          // Also drop any URL-only carry-forward inputs for this filter.
          self.predictiveSearchResults.querySelectorAll(
            'input[data-aro-carry="1"][name="' + CSS.escape(name) + '"][value="' + CSS.escape(value) + '"]'
          ).forEach(function (h) { h.remove(); });
          // Re-capture full filter state so the removed filter doesn't
          // get restored by _restoreFilterState on the next render.
          self._captureFilterState();
          self.onFilterChange();
        });
      });
      this.predictiveSearchResults.querySelectorAll('[data-aro-clear-filters]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.predictiveSearchResults.querySelectorAll('[data-aro-filter]').forEach(function (inp) {
            if (inp.type === 'checkbox') inp.checked = false;
            else inp.value = '';
          });
          // Drop any URL-only carry-forward inputs too.
          self.predictiveSearchResults.querySelectorAll('input[data-aro-carry="1"]').forEach(function (n) { n.remove(); });
          // Capture the now-empty state so nothing is restored after render.
          self._captureFilterState();
          self.onFilterChange();
        });
      });

      // PR-C5f: price buckets are single-select — checking one unchecks siblings.
      // Prevents non-adjacent range combinations (which would produce nonsensical
      // gte/lte pairs anyway).
      this.predictiveSearchResults.querySelectorAll('[data-aro-price-bucket]').forEach(function (bucket) {
        bucket.addEventListener('change', function () {
          if (!bucket.checked) return;
          self.predictiveSearchResults.querySelectorAll('[data-aro-price-bucket]').forEach(function (other) {
            if (other !== bucket) other.checked = false;
          });
        });
      });

      // PR-C5f: price-range chip removal — unchecks all price bucket checkboxes
      // and re-fetches. Same UX as removing a regular filter chip.
      this.predictiveSearchResults.querySelectorAll('[data-aro-remove-price-range]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.predictiveSearchResults.querySelectorAll('[data-aro-price-bucket]').forEach(function (inp) {
            inp.checked = false;
          });
          self.onFilterChange();
        });
      });

      // PR-C5c: prevent focus loss on non-focusable interaction targets (filter
      // header <div>s, chip buttons, labels). mousedown preventDefault keeps focus
      // on whatever currently has it (search input), so focusout doesn't fire and
      // trigger close. Click still fires as normal. Skip if the click lands
      // directly on an <input> — that needs focus to receive checkbox toggle.
      var noBlurSelectors = [
        '[data-aro-filter-toggle]',
        '[data-aro-recipe-filter-toggle]',
        '[data-aro-remove-filter]',
        '[data-aro-remove-price-range]',
        '[data-aro-clear-filters]',
        '.aro-search-dropdown__facet',
        // PR-C9a: tab labels — clicking Blogs/Recipes tab focused the hidden
        // (off-screen, pointer-events:none) radio which browsers may not focus
        // on, causing activeElement to fall to <body> → focusout close. Same
        // mousedown-preventDefault trick keeps focus on the search input while
        // still allowing the label[for=X] click to toggle the radio.
        '.aro-search-dropdown__tab',
      ].join(', ');
      this.predictiveSearchResults.querySelectorAll(noBlurSelectors).forEach(function (el) {
        el.addEventListener('mousedown', function (e) {
          if (e.target.matches('input, textarea, select')) return;
          e.preventDefault();
        });
      });

      // PR-C5d: after any re-render, restore focus to the search input. Fixes
      // "checkbox check closes dropdown" — when the checkbox change triggered
      // a re-fetch → innerHTML replace, the focused checkbox was detached from
      // the DOM. activeElement fell back to <body>, focusout fired on the form,
      // setTimeout activeElement check saw activeElement not in form → close.
      // Refocusing the input keeps activeElement inside the form so close is
      // correctly skipped.
      // Only restore focus if the dropdown is still meant to be open.
       // Race guard: if user pressed Enter (Phase 8d AJAX intercept
       // closed the dropdown via searchForm.close() → sets inert) while
       // a keystroke-triggered getSearchResults was in flight, the late
       // response would refocus input → focus handler re-fetches typing
       // state → dropdown reopens on top of the just-loaded /search page.
      if (
        this.input &&
        document.activeElement !== this.input &&
        !this.predictiveSearchResults.hasAttribute('inert')
      ) {
        this.input.focus({ preventScroll: true });
      }
    }

    // Custom Availability facet — In stock (filter.v.availability=1) and
    // Out of stock (filter.p.tag=_fully-oos, product-level OOS) are
    // mutually exclusive. Uncheck the sibling when one is checked so the
    // URL never contains contradictory params. Mirrors the /search page's
    // syncAvailabilityCheckbox in aro-search-page-filters.js.
    _enforceAvailabilityMutex(inp) {
      if (!inp.checked) return;
      var siblingAttr = inp.hasAttribute('data-aro-availability-oos') ? 'data-aro-availability-instock'
                      : inp.hasAttribute('data-aro-availability-instock') ? 'data-aro-availability-oos'
                      : null;
      if (!siblingAttr) return;
      var other = this.predictiveSearchResults.querySelector('input[' + siblingAttr + ']');
      if (other && other.checked) other.checked = false;
    }

    // Capture / restore ALL filter selections across renders. Zero-result
    // fetches drop non-matching filters from Shopify's search.filters
    // response — the fresh Liquid render then loses:
    //   - the checkbox `checked` state (Shopify's value.active is what sets it)
    //   - the chip in the active-filters row (also driven by value.active)
    // Without JS state persistence, applying a filter that narrows to 0
    // results silently drops OTHER filters on the next fetch.
    //
    // Approach: capture DOM state (all checked filter inputs) before every
    // onFilterChange, then after each render re-check matching inputs OR
    // inject a hidden data-aro-carry input if the checkbox is missing from
    // the fresh render. Mirrors the /search page's carry-forward pattern
    // in aro-search-page-filters.js.
    _captureFilterState() {
      this._filterState = {};
      var self = this;
      this.predictiveSearchResults.querySelectorAll('[data-aro-filter]').forEach(function (inp) {
        // Price buckets are managed separately by data-aro-price-bucket handling.
        if (inp.hasAttribute('data-aro-price-bucket')) return;
        // Only track currently-checked (or non-checkbox with a value) inputs.
        if (inp.type === 'checkbox' && !inp.checked) return;
        if (!inp.name || !inp.value) return;
        if (!self._filterState[inp.name]) self._filterState[inp.name] = [];
        self._filterState[inp.name].push(inp.value);
      });
    }

    // Inject an Availability chip when the corresponding checkbox is
    // checked but Shopify's server-side chip loop didn't emit one.
    // Shopify's filter.values inconsistently exposes hidden underscore-
    // prefixed tags (like _fully-oos) as active. Called from the same
    // post-render init as _restoreFilterState so a chip appears every
    // time the OOS/In stock state is applied.
    _syncAvailabilityChip() {
      var chipsRow = this.predictiveSearchResults.querySelector('.aro-search-dropdown__filter-chips');
      if (!chipsRow) return;
      // Clear any prior JS-injected availability chip before deciding.
      chipsRow.querySelectorAll('[data-aro-availability-chip]').forEach(function (n) { n.remove(); });

      var self = this;
      var oosCb = this.predictiveSearchResults.querySelector('input[data-aro-availability-oos]');
      var inCb  = this.predictiveSearchResults.querySelector('input[data-aro-availability-instock]');
      var candidates = [];
      if (oosCb && oosCb.checked) candidates.push({ name: 'filter.p.tag', value: '_fully-oos', label: 'Out of stock' });
      if (inCb  && inCb.checked)  candidates.push({ name: 'filter.v.availability', value: '1', label: 'In stock' });

      candidates.forEach(function (c) {
        // Skip if Shopify already rendered this chip server-side.
        var existing = chipsRow.querySelector(
          '[data-aro-remove-filter="' + c.name + '"][data-aro-remove-value="' + c.value + '"]:not([data-aro-availability-chip])'
        );
        if (existing) return;

        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'aro-search-dropdown__filter-chip';
        chip.setAttribute('data-aro-remove-filter', c.name);
        chip.setAttribute('data-aro-remove-value', c.value);
        chip.setAttribute('data-aro-availability-chip', '1');
        chip.setAttribute('aria-label', 'Remove filter: ' + c.label);
        chip.innerHTML = '<span>' + c.label + '</span><span class="aro-search-dropdown__filter-chip-x" aria-hidden="true">&times;</span>';

        var clearAll = chipsRow.querySelector('[data-aro-clear-filters]');
        if (clearAll) chipsRow.insertBefore(chip, clearAll); else chipsRow.appendChild(chip);

        // Bind removal handler manually — bindLiveFilters has already
        // iterated the pre-existing chips and won't see this new one.
        chip.addEventListener('click', function () {
          self.predictiveSearchResults.querySelectorAll(
            'input[data-aro-filter][name="' + CSS.escape(c.name) + '"][value="' + CSS.escape(c.value) + '"]'
          ).forEach(function (input) {
            if (input.type === 'checkbox') input.checked = false;
          });
          self.predictiveSearchResults.querySelectorAll(
            'input[data-aro-carry="1"][name="' + CSS.escape(c.name) + '"][value="' + CSS.escape(c.value) + '"]'
          ).forEach(function (h) { h.remove(); });
          self._captureFilterState();
          self.onFilterChange();
        });
      });
    }

    _restoreFilterState() {
      if (!this._filterState) return;
      // Clear any prior data-aro-carry hidden inputs; we'll re-add fresh
      // ones for filters still applied but missing from the current render.
      this.predictiveSearchResults.querySelectorAll('input[data-aro-carry="1"]').forEach(function (n) { n.remove(); });
      var self = this;
      Object.keys(this._filterState).forEach(function (name) {
        self._filterState[name].forEach(function (value) {
          var input = self.predictiveSearchResults.querySelector(
            'input[data-aro-filter][name="' + CSS.escape(name) + '"][value="' + CSS.escape(value) + '"]'
          );
          if (input) {
            if (input.type === 'checkbox') input.checked = true;
          } else {
            // Checkbox for this filter isn't rendered in the fresh DOM
            // (Shopify dropped it from filter.values because narrowing
            // to 0 hides low-count values). Inject a hidden carry input
            // so _collectActiveFilterParams still includes it next fetch.
            var hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = name;
            hidden.value = value;
            hidden.setAttribute('data-aro-filter', '');
            hidden.setAttribute('data-aro-carry', '1');
            self.predictiveSearchResults.appendChild(hidden);
          }
        });
      });
    }

    // Collect currently-checked filter inputs and append them to a
    // URLSearchParams. Extracted so both onFilterChange (explicit filter
    // click) and getSearchResults (query/focus refresh) can preserve
    // filter state on every fetch — see PR-Dropdown-preserve-filter.
    _collectActiveFilterParams(params) {
      var inputs = this.predictiveSearchResults.querySelectorAll('[data-aro-filter]');
      inputs.forEach(function (inp) {
        // PR-C5f: price bucket checkboxes — emit gte/lte from data attrs.
        // No name/value, controlled entirely by data-aro-price-gte/lte in subunit
        // (pence for GBP, cents for USD, etc.). Values agnostic to currency.
        if (inp.type === 'checkbox' && inp.hasAttribute('data-aro-price-bucket')) {
          if (!inp.checked) return;
          var gte = inp.getAttribute('data-aro-price-gte');
          var lte = inp.getAttribute('data-aro-price-lte');
          if (gte) params.append('filter.v.price.gte', gte);
          if (lte) params.append('filter.v.price.lte', lte);
          return;
        }
        if (!inp.name) return;
        if (inp.type === 'checkbox') {
          if (inp.checked && inp.value) params.append(inp.name, inp.value);
        } else if (inp.value) {
          params.append(inp.name, inp.value);
        }
      });
    }

    // PR-C5: re-fetch section with current filter selections, re-render products
    // in place. Filter state is preserved because Shopify sets value.active on
    // the response — the fresh Liquid render checks each checkbox that matches.
    //
    // Stale-request guard: rapid checkbox toggling could fire fetch A then
    // fetch B; if B resolves before A, then A's late response would clobber
    // the DOM with stale state. Monotonic ID per call — response ignored if
    // a newer request has started. Mirrors pattern in aro-search-page-{products,
    // blogs,recipes}.js.
    onFilterChange() {
      var searchTerm = this.getQuery();
      if (!searchTerm) return;
      this.predictiveSearchResults.classList.add('loading');

      var params = new URLSearchParams();
      params.append('q', searchTerm);
      params.append('section_id', 'predictive-search');
      this._collectActiveFilterParams(params);

      var url = theme.routes.search_url + '?' + params.toString();
      var self = this;
      var myId = (this._filterFetchId = (this._filterFetchId || 0) + 1);
      fetch(url).then(function (r) {
        if (myId !== self._filterFetchId) return null;  // stale — newer fetch started
        self.predictiveSearchResults.classList.remove('loading');
        if (!r.ok) throw new Error('filter fetch HTTP ' + r.status);
        return r.text();
      }).then(function (text) {
        if (text === null || myId !== self._filterFetchId) return;  // stale — skip render
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var section = doc.querySelector('#shopify-section-predictive-search');
        if (section) {
          self.renderSearchResults(section.innerHTML);
          // PR-C15: restore JS-rendered Blogs/Recipes panels from cached
          // article data. Product filters change /search results (which
          // re-renders the whole section, including Liquid's default blog/
          // recipe panels) — but Blogs/Recipes must stay independent.
          if (self._lastArticles !== undefined) {
            self.renderArticlePanels(
              self._lastArticles,
              self._lastTagMatchedRecipes || [],
              self._lastTagMatchedBlogs || [],
              self._lastSearchTerm || ''
            );
          }
        }
      }).catch(function (err) {
        if (myId !== self._filterFetchId) return;  // stale error — don't spam console
        console.warn('[predictive-search] filter fetch failed:', err);
      });
    }

    // PR-C9b: override the Blogs / Recipes panel contents with data from
    // /search/suggest.json?resources[type]=article. The section pagination
    // (mixed products+articles, 50 per page) frequently drops blog articles
    // Parse the article-only section fetch (?q=X&type=article&section_id=
    // predictive-search) into article objects. Section server-renders full
    // blogs + recipes panels — we extract each .aro-search-dropdown__article
    // link and convert it back to the JSON shape renderArticlePanels expects.
    // _htmlSource flag lets scopesFor default missing scopes to true so the
    // scope filter doesn't hide these articles wholesale.
    extractArticlesFromSection(sectionHtml) {
      if (!sectionHtml) return [];
      var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      var links = doc.querySelectorAll(
        '.aro-search-dropdown__panel--blogs .aro-search-dropdown__article,' +
        ' .aro-search-dropdown__panel--recipes .aro-search-dropdown__article'
      );
      var out = [];
      links.forEach(function (a) {
        var url = a.getAttribute('href') || '';
        var titleEl = a.querySelector('.aro-search-dropdown__article-title');
        var imgEl = a.querySelector('.aro-search-dropdown__article-image img');
        var timeEl = a.querySelector('time');
        var metaEl = a.querySelector('.aro-search-dropdown__article-meta');
        var published = '';
        if (timeEl) published = timeEl.getAttribute('datetime') || timeEl.textContent.trim();
        else if (metaEl) published = metaEl.textContent.trim();
        out.push({
          url: url,
          title: titleEl ? titleEl.textContent : '',
          image: imgEl ? (imgEl.getAttribute('src') || imgEl.getAttribute('data-src') || '') : '',
          published_at: published,
          summary: '',
          body: '',
          tags: [],
          recipe_data: null,
          _htmlSource: true
        });
      });
      return out;
    }

    // when products dominate; the article-specific fetch always returns the
    // top article matches regardless. Safe no-op if the panels aren't present.
    //
    // PR-C10: for each RECIPE, classify which scope the search term matched:
    //   title | description | body | products (tags used as product proxy).
    //   Stored as `data-scope-*` attrs on the article link so the recipe-scope
    //   filter checkboxes can hide/show without another fetch.
    renderArticlePanels(articles, tagMatchedRecipes, tagMatchedBlogs, searchTerm) {
      var blogPrefix = '/blogs/natural-organic-skincare-blog/';
      var recipePrefix = '/blogs/make-your-own-skin-care-recipes/';
      // PR-C13: blogs merged the same way as recipes — Shopify suggest + tag fetch.
      var shopifyBlogs = articles.filter(function (a) { return (a.url || '').indexOf(blogPrefix) !== -1; });
      var blogByUrl = {};
      shopifyBlogs.forEach(function (a) { blogByUrl[a.url.split('?')[0]] = a; });
      (tagMatchedBlogs || []).forEach(function (t) {
        var key = (t.url || '').split('?')[0];
        if (!blogByUrl[key]) blogByUrl[key] = t;
      });
      var blogs = Object.keys(blogByUrl).map(function (k) { return blogByUrl[k]; });
      // PR-C11 (revised): merge two recipe sources.
      var shopifyRecipes = articles.filter(function (a) { return (a.url || '').indexOf(recipePrefix) !== -1; });
      var byUrl = {};
      shopifyRecipes.forEach(function (a) { byUrl[a.url.split('?')[0]] = a; });
      tagMatchedRecipes.forEach(function (t) {
        var key = (t.url || '').split('?')[0];
        if (!byUrl[key]) byUrl[key] = t;
      });
      var recipes = Object.keys(byUrl).map(function (k) { return byUrl[k]; });
      var qLower = (searchTerm || '').toLowerCase().trim();
      var qEsc = (searchTerm || '').replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
      function stripHtml(s) {
        return (s || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');
      }
      function scopesFor(a) {
        // HTML-sourced articles (from the article-section fetch) don't carry
        // summary/body/tags/recipe_data — only url/title/image/date extracted
        // from the rendered link. Without full text we can't do a precise
        // classification, so default the missing scopes to TRUE so scope
        // filters don't hide them wholesale. Tag-fetched articles have the
        // full data and get precise classification below.
        if (a._htmlSource) {
          var t = (a.title || '').toLowerCase();
          return {
            title: t.indexOf(qLower) !== -1,
            description: true,
            body: true,
            products: true
          };
        }
        // PR-C12a: each scope UNIONS every available source rather than
        // preferring one. Ensures fallbacks actually augment coverage —
        // a recipe with recipe_data whose search term appears in body
        // intro text (not methodSteps) still matches Body scope.
        //
        // Sources:
        //   Title:  article.title + recipe_data.name
        //   Desc:   article.summary + summary_html (both stripped)
        //   Body:   recipe_data.methodSteps + native body (stripped)
        //   Prods:  recipe_data.phases[].ingredients[].{name,productHandle}
        //           + article.tags[]
        var rd = a.recipe_data || null;

        // Title — combine both sources, single substring check.
        var titleText = ((a.title || '') + ' ' + (rd && rd.name || '')).toLowerCase();
        var title = titleText.indexOf(qLower) !== -1;

        // Description — union summary and summary_html.
        var descText = (stripHtml(a.summary || '') + ' ' + stripHtml(a.summary_html || '')).toLowerCase();
        var description = descText.indexOf(qLower) !== -1;

        // Body — union native body and methodSteps.
        var bodyPieces = [];
        if (rd && rd.methodSteps && rd.methodSteps.length) bodyPieces.push(rd.methodSteps.join(' '));
        if (a.body) bodyPieces.push(stripHtml(a.body));
        var body = bodyPieces.join(' ').toLowerCase().indexOf(qLower) !== -1;

        // Products — union structured ingredients and tags.
        var products = false;
        if (rd && rd.phases && rd.phases.length) {
          products = rd.phases.some(function (phase) {
            return (phase.ingredients || []).some(function (ing) {
              var n = (ing.name || '').toLowerCase();
              var h = (ing.productHandle || '').toLowerCase();
              return n.indexOf(qLower) !== -1 || (h && h.indexOf(qLower) !== -1);
            });
          });
        }
        if (!products) {
          products = (a.tags || []).some(function (t) { return (t || '').toLowerCase().indexOf(qLower) !== -1; });
        }
        return { title: title, description: description, body: body, products: products };
      }
      function articleRow(a, isRecipe) {
        var img = a.image ? '<div class="aro-search-dropdown__article-image"><img src="' + a.image + '" alt="" loading="lazy"></div>'
                          : '<div class="aro-search-dropdown__article-image thb-placeholder"></div>';
        var date = '';
        if (a.published_at) {
          try {
            var d = new Date(a.published_at);
            var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            date = months[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear();
          } catch (e) { /* skip date */ }
        }
        var scopeAttrs = '';
        if (isRecipe) {
          var s = scopesFor(a);
          scopeAttrs = ' data-scope-title="' + (s.title ? 1 : 0) + '"' +
                       ' data-scope-description="' + (s.description ? 1 : 0) + '"' +
                       ' data-scope-body="' + (s.body ? 1 : 0) + '"' +
                       ' data-scope-products="' + (s.products ? 1 : 0) + '"';
        }
        return '<a href="' + a.url + '" class="aro-search-dropdown__article"' + scopeAttrs + '>' +
          img +
          '<div class="aro-search-dropdown__article-body">' +
            '<div class="aro-search-dropdown__article-title">' + (a.title || '') + '</div>' +
          '</div></a>';
      }
      function panelHtml(items, singular, isRecipe) {
        if (items.length === 0) {
          return '<div class="aro-search-dropdown__empty">No ' + singular.toLowerCase() + 's found for \'' + qEsc + '\'</div>';
        }
        return '<div class="aro-search-dropdown__count">' + singular + 's For \'' + qEsc + '\'</div>' +
               '<div class="aro-search-dropdown__articles">' +
                 // Recipes 9, Blogs 12. Recipes may be further hidden by
                 // scope-filter unchecks. Tomas 2026-08-06.
                 items.slice(0, isRecipe ? 9 : 12).map(function (a) { return articleRow(a, isRecipe); }).join('') +
               '</div>';
      }
      var blogPanel = this.predictiveSearchResults.querySelector('.aro-search-dropdown__panel--blogs');
      var recipePanel = this.predictiveSearchResults.querySelector('.aro-search-dropdown__panel--recipes');
      if (blogPanel) blogPanel.innerHTML = panelHtml(blogs, 'Blog', false);
      if (recipePanel) recipePanel.innerHTML = panelHtml(recipes, 'Recipe', true);
      // After render: apply current scope filter state to visibility.
      this.applyRecipeScopeFilter();
    }

    // PR-C10: recipe-scope filter — reads which of the 4 scope checkboxes are
    // currently ticked and hides recipe rows whose data-scope-* flags don't
    // include at least one ticked scope. Updates the visible count. No fetch.
    applyRecipeScopeFilter() {
      var recipePanel = this.predictiveSearchResults.querySelector('.aro-search-dropdown__panel--recipes');
      if (!recipePanel) return;
      var scopes = this.getRecipeScopeState();
      var visible = 0;
      // Matches the recipe panel render cap in panelHtml() (items.slice
      // 0, isRecipe ? 9 : 12). Kept in lockstep so scope filter doesn't
      // silently hide items the panel renders. Tomas 2026-08-06.
      var maxVisible = 9;
      var rows = recipePanel.querySelectorAll('.aro-search-dropdown__article');
      rows.forEach(function (row) {
        var include = false;
        if (scopes.title && row.getAttribute('data-scope-title') === '1') include = true;
        if (!include && scopes.description && row.getAttribute('data-scope-description') === '1') include = true;
        if (!include && scopes.body && row.getAttribute('data-scope-body') === '1') include = true;
        if (!include && scopes.products && row.getAttribute('data-scope-products') === '1') include = true;
        if (include && visible < maxVisible) {
          row.style.display = '';
          visible++;
        } else {
          row.style.display = 'none';
        }
      });
      // (No count display — panel count line just shows "Results For 'X'".)
    }
    getRecipeScopeState() {
      // Default: all scopes on. this.recipeScopes is set from checkbox change events.
      var defaults = { title: true, description: true, body: true, products: true };
      return this.recipeScopes || defaults;
    }

    // PR-C11 (revised): tag list is a small blob emitted at page load —
    // [{ name: 'Sweet Almond Oil', handle: 'sweet-almond-oil' }, ...]. Parsed
    // once and cached on the instance.
    getRecipeTagsData() {
      if (this._recipeTags) return this._recipeTags;
      var el = document.getElementById('aro-recipe-tags');
      if (!el) { this._recipeTags = []; return this._recipeTags; }
      try { this._recipeTags = JSON.parse(el.textContent) || []; }
      catch (e) {
        console.warn('[predictive-search] aro-recipe-tags JSON parse failed:', e);
        this._recipeTags = [];
      }
      return this._recipeTags;
    }
    // Substring match tag names against the search term. Returns handles for
    // constructing tagged-blog URLs.
    getMatchingTagHandles(searchTerm) {
      var q = (searchTerm || '').toLowerCase().trim();
      if (!q) return [];
      return this.getRecipeTagsData()
        .filter(function (t) { return (t.name || '').toLowerCase().indexOf(q) !== -1; })
        .map(function (t) { return t.handle; });
    }
    // Fire one parallel fetch per matched tag. Each returns article JSON
    // (from sections/predictive-search-tagged-recipes.liquid). Flat-concat
    // the results and return them. Called from Promise.all in getSearchResults.
    fetchTagMatchedRecipes(searchTerm) {
      var handles = this.getMatchingTagHandles(searchTerm);
      if (handles.length === 0) return Promise.resolve([]);
      var recipesBlog = '/blogs/make-your-own-skin-care-recipes';
      var section = 'predictive-search-tagged-recipes';
      var fetches = handles.map(function (h) {
        var url = recipesBlog + '/tagged/' + encodeURIComponent(h) + '?section_id=' + section;
        return fetch(url).then(function (r) {
          if (!r.ok) return [];
          return r.text();
        }).then(function (html) {
          if (!html) return [];
          // Shopify's section rendering API wraps our JSON in
          // <div id="shopify-section-<name>">…</div>. Peel it off.
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var sec = doc.getElementById('shopify-section-' + section);
          if (!sec) return [];
          try { return JSON.parse(sec.textContent.trim()) || []; }
          catch (e) { return []; }
        }).catch(function () { return []; });
      });
      return Promise.all(fetches).then(function (arrays) {
        return arrays.reduce(function (acc, arr) { return acc.concat(arr); }, []);
      });
    }

    // PR-C13: mirror of the recipe tag-fetch machinery for the Blogs tab.
    // Reads #aro-blog-tags, substring-matches, then fires one fetch per
    // matching tag against the natural-organic-skincare-blog. Same section
    // endpoint (recipe_data/recipe_jsonld just come back null for blog
    // articles — JS scopesFor handles that gracefully).
    getBlogTagsData() {
      if (this._blogTags) return this._blogTags;
      var el = document.getElementById('aro-blog-tags');
      if (!el) { this._blogTags = []; return this._blogTags; }
      try { this._blogTags = JSON.parse(el.textContent) || []; }
      catch (e) {
        console.warn('[predictive-search] aro-blog-tags JSON parse failed:', e);
        this._blogTags = [];
      }
      return this._blogTags;
    }
    getMatchingBlogTagHandles(searchTerm) {
      var q = (searchTerm || '').toLowerCase().trim();
      if (!q) return [];
      return this.getBlogTagsData()
        .filter(function (t) { return (t.name || '').toLowerCase().indexOf(q) !== -1; })
        .map(function (t) { return t.handle; });
    }
    fetchTagMatchedBlogArticles(searchTerm) {
      var handles = this.getMatchingBlogTagHandles(searchTerm);
      if (handles.length === 0) return Promise.resolve([]);
      var blog = '/blogs/natural-organic-skincare-blog';
      var section = 'predictive-search-tagged-recipes';  // section reused; blogs get null recipe_data
      var fetches = handles.map(function (h) {
        var url = blog + '/tagged/' + encodeURIComponent(h) + '?section_id=' + section;
        return fetch(url).then(function (r) {
          if (!r.ok) return [];
          return r.text();
        }).then(function (html) {
          if (!html) return [];
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var sec = doc.getElementById('shopify-section-' + section);
          if (!sec) return [];
          try { return JSON.parse(sec.textContent.trim()) || []; }
          catch (e) { return []; }
        }).catch(function () { return []; });
      });
      return Promise.all(fetches).then(function (arrays) {
        return arrays.reduce(function (acc, arr) { return acc.concat(arr); }, []);
      });
    }

    // PR-C6: inject autocomplete queries into the SUGGESTIONS placeholder that
    // sections/predictive-search.liquid renders in the LEFT column. Safe no-op
    // if the placeholder isn't present (mobile / smaller layouts).
    renderQueries(queries) {
      var mount = this.querySelector('.aro-search-dropdown__suggestions-mount');
      if (!mount || !queries || queries.length === 0) return;
      var items = queries.slice(0, 5).map(function (q) {
        var text = q.styled_text || q.text || '';
        var url = q.url || '/search?q=' + encodeURIComponent(q.text || '');
        return '<a href="' + url + '" class="aro-search-dropdown__suggestion">' +
          '<svg class="aro-search-dropdown__suggestion-icon" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><circle cx="5" cy="5" r="3.5" fill="none" stroke="currentColor" stroke-width="1.2"/><line x1="7.5" y1="7.5" x2="10.5" y2="10.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>' +
          '<span>' + text + '</span>' +
          '</a>';
      }).join('');
      mount.innerHTML =
        '<div class="aro-search-dropdown__label aro-search-dropdown__label--suggestions">Suggestions</div>' +
        '<div class="aro-search-dropdown__suggestions">' + items + '</div>';
    }

    // "Did you mean X?" inside the products-panel empty state. Only fires
    // when the products panel is empty (no product-card children) AND
    // Shopify's suggest.json returned a query suggestion that differs
    // from the typed term. Reuses the queries array already fetched.
    _populateDidYouMean(searchTerm, queries) {
      var target = this.querySelector('.aro-search-dropdown__panel--products [data-aro-did-you-mean]');
      if (!target) return; // Products panel isn't in empty state
      var typed = (searchTerm || '').trim().toLowerCase();
      var pick = null;
      for (var i = 0; i < queries.length; i++) {
        var t = ((queries[i].text || queries[i].title) || '').trim();
        if (t && t.toLowerCase() !== typed) { pick = t; break; }
      }
      if (!pick) return;
      target.innerHTML = '';
      target.appendChild(document.createTextNode('Did you mean '));
      var link = document.createElement('a');
      link.href = '/search?q=' + encodeURIComponent(pick);
      link.className = 'aro-did-you-mean__link';
      link.textContent = '"' + pick + '"';
      target.appendChild(link);
      target.appendChild(document.createTextNode('?'));
      target.removeAttribute('hidden');
    }

    close() {
      this.predictiveSearchResults.setAttribute('inert', '');
      document.body.classList.remove('open-search');
    }
    open() {
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
    }
  }
  customElements.define('search-form', SearchForm);
}